import { isCollection, isMap, isScalar, isSeq, parseDocument, Scalar, stringify } from "yaml";
import type { Document, YAMLMap } from "yaml";
import type { JsonObject } from "@/api/common";
import { parseOverrideKey } from "./recipeOverrides";
import {
  applyOverridesToYaml,
  canSetPath,
  overridePathTokens,
  type YamlPathKey,
} from "./recipeYaml";

// override 를 본문의 원문 텍스트에 끼워 넣는다. 사용자가 편집할 본문을 만드는 자리에서 쓴다.
//
// 보장:
//   - 결과의 값은 `applyOverridesToYaml` 과 같다. 텍스트로 끼운 결과를 그 함수의 결과와 비교하고,
//     다르면 그 함수의 결과를 쓴다. 그래서 이 모듈은 값을 정하지 않고 표기만 정한다.
//   - 텍스트로 끼운 override 는 자기 줄만 바꾼다. 다른 줄은 원문과 같다.
//
// 텍스트로 끼우지 못하는 override 는 본문 전체를 다시 직렬화한다(`applyOverridesToYaml`). 예: 블록
// 컬렉션을 통째로 바꾸는 값, flow 컬렉션 안의 삭제, 컬렉션의 마지막 항목 삭제, 여러 줄 값.
export function patchOverridesIntoYaml(body: string, overrides: JsonObject): string {
  if (parseDocument(body).errors.length > 0) {
    return body;
  }
  let source = body;
  for (const [key, value] of Object.entries(overrides)) {
    source = patchOverride(source, key, value);
  }
  return source;
}

function patchOverride(source: string, key: string, value: unknown): string {
  const doc = parseDocument(source);
  const { op, path } = parseOverrideKey(key);
  const keys = overridePathTokens(path);
  // 없는 경로의 삭제와 쓸 수 없는 경로는 본문을 바꾸지 않는다.
  if (op === "delete" ? !doc.hasIn(keys) : !canSetPath(doc, keys)) {
    return source;
  }
  const reserialized = applyOverridesToYaml(source, { [key]: value });
  const patched =
    op === "delete" ? deleteText(source, doc, keys) : setText(source, doc, keys, value);
  return patched !== null && hasSameData(patched, reserialized) ? patched : reserialized;
}

function hasSameData(left: string, right: string): boolean {
  const leftDoc = parseDocument(left);
  const rightDoc = parseDocument(right);
  if (leftDoc.errors.length > 0 || rightDoc.errors.length > 0) {
    return false;
  }
  return JSON.stringify(leftDoc.toJS()) === JSON.stringify(rightDoc.toJS());
}

// 값을 한 줄 텍스트로 만든다. 컬렉션은 flow 로 쓴다. 한 줄로 쓸 수 없으면 null.
const VALUE_TEXT_OPTIONS = {
  collectionStyle: "flow",
  flowCollectionPadding: false,
  lineWidth: 0,
} as const;

function valueText(value: unknown): string | null {
  const text = stringify(value, VALUE_TEXT_OPTIONS).replace(/\n$/, "");
  return text.includes("\n") ? null : text;
}

// 문자열로 바꿀 때는 원래 값의 따옴표 종류를 유지한다.
function replacementText(value: unknown, existing: Scalar): string | null {
  const keepsQuote = existing.type === Scalar.QUOTE_DOUBLE || existing.type === Scalar.QUOTE_SINGLE;
  if (typeof value !== "string" || !keepsQuote) {
    return valueText(value);
  }
  const scalar = new Scalar(value);
  scalar.type = existing.type;
  return valueText(scalar);
}

function setText(
  source: string,
  doc: Document,
  keys: readonly YamlPathKey[],
  value: unknown,
): string | null {
  const target = doc.getIn(keys, true);
  if (target === undefined) {
    return insertText(source, doc, keys, value);
  }
  if (isScalar(target) && target.range) {
    const [start, end] = target.range;
    const text = replacementText(value, target);
    if (text === null || source.slice(start, end).includes("\n")) {
      return null;
    }
    return source.slice(0, start) + padEmptyValue(source, start, end, text) + source.slice(end);
  }
  if (isCollection(target) && target.flow && target.range) {
    const [start, end] = target.range;
    const text = valueText(value);
    if (text === null || source.slice(start, end).includes("\n")) {
      return null;
    }
    return source.slice(0, start) + text + source.slice(end);
  }
  return null;
}

// 값이 빈 키(`key:`)는 범위의 길이가 0 이다. 콜론과 뒤 주석에서 값을 띄운다.
function padEmptyValue(source: string, start: number, end: number, text: string): string {
  if (start !== end) {
    return text;
  }
  const before = source[start - 1] === ":" ? " " : "";
  const next = source[start];
  const after = next === undefined || next === "\n" || next === "\r" ? "" : " ";
  return `${before}${text}${after}`;
}

// 없는 경로에 쓴다. 가장 깊은 기존 블록 map 의 내용 끝에 줄을 넣는다.
function insertText(
  source: string,
  doc: Document,
  keys: readonly YamlPathKey[],
  value: unknown,
): string | null {
  let depth = keys.length - 1;
  while (depth > 0 && doc.getIn(keys.slice(0, depth), true) === undefined) {
    depth -= 1;
  }
  const ancestor = depth === 0 ? doc.contents : doc.getIn(keys.slice(0, depth), true);
  const missing = keys.slice(depth);
  const text = valueText(value);
  if (!isBlockMap(ancestor) || text === null || missing.some((key) => typeof key === "number")) {
    return null;
  }
  const indent = keyIndent(source, ancestor);
  const end = contentEnd(source, ancestor);
  if (indent === null || end === null) {
    return null;
  }
  const lines = missing.map((key, index) => {
    const isLeaf = index === missing.length - 1;
    return `${indent}${"  ".repeat(index)}${valueText(String(key))}:${isLeaf ? ` ${text}` : ""}\n`;
  });
  const head = source.slice(0, end);
  const lineBreak = head === "" || head.endsWith("\n") ? "" : "\n";
  return head + lineBreak + lines.join("") + source.slice(end);
}

function deleteText(source: string, doc: Document, keys: readonly YamlPathKey[]): string | null {
  const parent = keys.length === 1 ? doc.contents : doc.getIn(keys.slice(0, -1), true);
  const last = keys[keys.length - 1];
  // 마지막 항목을 지우면 빈 컬렉션(`{}`·`[]`)이 남아야 한다. 줄을 지우면 값이 null 이 된다.
  if (isBlockMap(parent) && parent.items.length > 1) {
    const pair = parent.items.find((item) =>
      isScalar(item.key) ? item.key.value === last : item.key === last,
    );
    if (!pair || !isScalar(pair.key) || !pair.key.range) {
      return null;
    }
    const start = lineStart(source, pair.key.range[0]);
    const end = contentEnd(source, pair.value);
    // 키가 seq 의 `-` 와 같은 줄에 있으면 줄을 지울 수 없다.
    if (end === null || source.slice(start, pair.key.range[0]).trim() !== "") {
      return null;
    }
    return source.slice(0, start) + source.slice(end);
  }
  if (isSeq(parent) && !parent.flow && parent.items.length > 1 && typeof last === "number") {
    const item = parent.items[last];
    const itemStart = rangeStart(item);
    const end = contentEnd(source, item);
    if (itemStart === null || end === null) {
      return null;
    }
    const start = lineStart(source, itemStart);
    if (!/^ *- +$/.test(source.slice(start, itemStart))) {
      return null;
    }
    return source.slice(0, start) + source.slice(end);
  }
  return null;
}

function isBlockMap(node: unknown): node is YAMLMap {
  return isMap(node) && !node.flow && node.items.length > 0;
}

function rangeStart(node: unknown): number | null {
  return (isScalar(node) || isCollection(node)) && node.range ? node.range[0] : null;
}

// map 의 키 앞 들여쓰기. seq 아래 경로는 `canSetPath` 가 막는다. 그래서 첫 키는 줄의 처음에 있다.
function keyIndent(source: string, map: YAMLMap): string | null {
  const start = rangeStart(map.items[0].key);
  if (start === null) {
    return null;
  }
  const prefix = source.slice(lineStart(source, start), start);
  return /^ *$/.test(prefix) ? prefix : null;
}

// 노드의 내용이 끝나는 줄의 끝. 블록 컬렉션의 `range` 는 뒤따르는 주석까지 포함한다. 그 주석은 다음
// 키의 것일 수 있다. 그래서 마지막 leaf 가 있는 줄의 끝을 쓴다.
function contentEnd(source: string, node: unknown): number | null {
  let leaf = node;
  while (isCollection(leaf) && !leaf.flow && leaf.items.length > 0) {
    leaf = isMap(leaf)
      ? leaf.items[leaf.items.length - 1].value
      : leaf.items[leaf.items.length - 1];
  }
  if (!(isScalar(leaf) || isCollection(leaf)) || !leaf.range) {
    return null;
  }
  return lineEnd(source, leaf.range[1]);
}

function lineStart(source: string, index: number): number {
  return index === 0 ? 0 : source.lastIndexOf("\n", index - 1) + 1;
}

function lineEnd(source: string, index: number): number {
  if (index > 0 && source[index - 1] === "\n") {
    return index;
  }
  const lineBreak = source.indexOf("\n", index);
  return lineBreak === -1 ? source.length : lineBreak + 1;
}
